import { PlusOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton } from "@mui/material";
import { message, Popconfirm, Switch } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import Card from "../../../components/common/Card";
import { STATUS_PILL_CLASSES } from "../../../theme/statusColors";
import MuiTable from "../../../components/common/MuiTable";
import AddModal from "../../../components/Language/AddModal";
import { Column } from "../../../utils/constants";

// Define the shape of our language data according to the API response
interface LanguageData {
  id: number;
  code: string;
  name: string;
  flag: string;
  active: boolean;
  as_default: boolean;
  created_date: string;
  last_modified_date: string;
}

const Language: React.FC = () => {
  const { t } = useTranslation();

  const [openModal, setOpenModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [isLoading, setLoadingTable] = useState(false);
  const [data, setDataTable] = useState<LanguageData[]>([]);
  const [modalId, setModalId] = useState<number>(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingDefault, setLoadingDefault] = useState<number | null>(null);
  const [loadingEdit, setLoadingEdit] = useState<number | null>(null);

  const header: Column[] = [
    {
      id: "id",
      label: t("common.table.id"),
      minWidth: 10,
      align: "center",
    },
    {
      id: "name",
      label: t("common.table.language_name"),
      minWidth: 150,
      align: "center",
      format: (value) => {
        return <div className="font-bold ">{value}</div>;
      },
    },
    {
      id: "code",
      label: t("common.table.language_short_name"),
      minWidth: 100,
      align: "center",
      format: (value) => {
        return <div className="font-bold">{value}</div>;
      },
    },
    {
      id: "flag",
      label: t("common.table.flag"),
      minWidth: 100,
      align: "center",
      format: (value: any) => {
        return (
          <img
            src={value}
            alt="language flag"
            className="w-12 h-12 rounded-lg m-auto"
          />
        );
      },
    },
    {
      id: "active",
      label: t("common.table.active"),
      minWidth: 20,
      align: "center",
      format: (value) => {
        return (
          <div
            className={`px-2 rounded-lg ${
              value ? STATUS_PILL_CLASSES.SUCCESS : STATUS_PILL_CLASSES.DRAFT
            }`}
          >
            {value ? "true" : "false"}
          </div>
        );
      },
    },
    {
      id: "default_switch",
      label: "Default",
      minWidth: 100,
      align: "center",
      format: (rowId: number) => {
        if (!rowId) return null;
        const currentRow = data.find(row => row.id === rowId);
        if (!currentRow) return null;
        
        return (
          <Switch
            checkedChildren="ON"
            unCheckedChildren="OFF"
            checked={currentRow.as_default === true}
            disabled={currentRow.as_default === true}
            loading={loadingDefault === rowId}
            onChange={() => handleSetDefault(rowId)}
          />
        );
      },
    },
    {
      id: "created_date",
      label: t("common.table.created_date"),
      minWidth: 100,
      align: "center",
      format: (value) => {
        return <div>{moment(value).format("YYYY/MM/DD HH:mm:ss")}</div>;
      },
    },
    {
      id: "action",
      label: t("common.table.actions"),
      minWidth: 100,
      align: "center",
      format: (id: number) => {
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            <Box
              size={"small"}
              component={IconButton}
              disabled={loadingDelete || loadingEdit === id}
              onClick={() => handleClickEdit(id)}
            >
              {loadingEdit === id ? (
                <span className="anticon" style={{ fontSize: 24 }}>
                  <span className="anticon-spin">
                    <svg viewBox="0 0 1024 1024" focusable="false" data-icon="loading" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                      <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                    </svg>
                  </span>
                </span>
              ) : (
                <EditIcon color="success" />
              )}
            </Box>
            <Popconfirm
              title={t("help_support.delete_text")}
              okText={t("btn.confirm")}
              cancelText={t("btn.cancel")}
              onConfirm={() => handleDelete(id)}
              okButtonProps={{
                style: { background: "#ba2156", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box size={"small"} component={IconButton}>
                <DeleteIcon color={"error"} />
              </Box>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  const handleSetDefault = async (id: number) => {
    try {
      setLoadingDefault(id);
      await axiosInstance.patch(`admin/setting/languages/${id}/default`);
      message.success("Default language updated successfully");
      getDataTable();
    } catch (error) {
      console.error("Error setting default language:", error);
      message.error("Failed to update default language");
    } finally {
      setLoadingDefault(null);
    }
  };

  const handleClickEdit = async (id: number) => {
    try {
      setLoadingEdit(id);
      const response = await axiosInstance.get(`admin/setting/languages/${id}`);
      console.log("Edit language response:", response.data);
      if (response.data?.data) {
        setModalId(id);
        setOpenModal(true);
      }
    } catch (error) {
      console.error("Error fetching language details:", error);
      message.error("Failed to fetch language details");
    } finally {
      setLoadingEdit(null);
    }
  };

  const getDataTable = async () => {
    setLoadingTable(true);
    try {
      const response = await axiosInstance.get("admin/setting/languages");
      console.log("Languages response:", response.data);
      if (response.data?.data) {
        const updatedData = response.data.data.map((item: LanguageData) => ({
          ...item,
          action: item.id,
          default_switch: item.id
        }));
        setDataTable(updatedData);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      message.error("Failed to fetch languages");
    } finally {
      setLoadingTable(false);
    }
  };

  const handleReloadData = () => {
    getDataTable();
  };

  useEffect(() => {
    getDataTable();
  }, []);

  const handleDelete = async (id: number) => {
    setLoadingDelete(true);
    await axiosInstance
      .delete(`admin/setting/languages/${id}`)
      .then(() => {
        getDataTable();
        setLoadingDelete(false);
        messageApi.open({
          type: "success",
          content: "Success!",
        });
      })
      .catch((error: any) => {
        console.log("Error deleting language:", error);
        setLoadingDelete(false);
        message.error("Failed to delete language");
      });
  };
  return (
    <Card>
      <Box textAlign={"right"} my={2}>
        <ActionButton
          startIcon={<PlusOutlined />}
          onClick={() => {setOpenModal(true); setModalId(0)}}
        >
          Add Language
        </ActionButton>
      </Box>
      <MuiTable header={header} data={data} isLoading={isLoading}></MuiTable>
      <AddModal
        open={openModal}
        setOpen={(value) => setOpenModal(value)}
        reloadDataEvent={handleReloadData}
        languageId={modalId}
      />
      {contextHolder}
    </Card>
  );
};
export default Language;
